import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { injectNbUiText } from '../../i18n/ui-text';

/**
 * `<nb-sidebar>` — the web/desktop left navigation column (Mockup `web.css .sidebar`).
 *
 * A 262px landmark column (`<nav>`) holding a {@link NbSideBrand} header, one or
 * more {@link NbSideNavSection} / {@link NbSideNavItem} groups and an optional
 * {@link NbSideFoot}. Set `admin` for the dark internal-tool variant
 * (`.sidebar.admin`): the host gains the `admin` class and every descendant
 * component re-colours itself off `:host-context(.sidebar.admin)`.
 *
 * @example
 * <nb-sidebar [admin]="false" ariaLabel="Hovedmeny">
 *   <nb-side-brand name="Solsiden" sub="Borettslag" mark="S" />
 *   <nb-side-nav-section>Oversikt</nb-side-nav-section>
 *   <nb-side-nav-item [active]="true"><nb-icon [icon]="Home"/> Hjem</nb-side-nav-item>
 *   <nb-side-foot> … </nb-side-foot>
 * </nb-sidebar>
 */
@Component({
  selector: 'nb-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sidebar',
    role: 'navigation',
    '[class.admin]': 'admin()',
    '[attr.aria-label]': 'label()',
  },
  template: `<ng-content />`,
  styleUrl: './sidebar.css',
})
export class NbSidebar {
  /** Dark "internal tool" variant (`.sidebar.admin`): repaints the column and its children. */
  readonly admin = input(false);
  /** Accessible name for the navigation landmark. */
  readonly ariaLabel = input<string | undefined>(undefined);
  private readonly uiText = injectNbUiText();
  /** The accessible name actually used — the input when given, else the app's design-system copy. */
  protected readonly label = computed(() => this.ariaLabel() ?? this.uiText().mainMenu);
}
